/**
 * Google Analytics 4 helper.
 *
 * Design:
 *   - The Measurement ID comes from REACT_APP_GA_MEASUREMENT_ID at build time.
 *   - GA fires ONLY on the production domain (micro-research.adrigoinsights.com).
 *     Preview/dev traffic is intentionally excluded so testing never pollutes
 *     bounce rate / session counts.
 *   - Route-change page views are sent via `trackPageview` — see the
 *     RouterAnalytics component in App.js which subscribes to react-router's
 *     useLocation() and calls trackPageview on every location change.
 */
const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";
const PROD_HOSTNAMES = new Set(["micro-research.adrigoinsights.com"]);

let initialized = false;

/** True only on the live production hostname (never dev/preview/localhost). */
const isProductionHost = () => {
  if (typeof window === "undefined") return false;
  return PROD_HOSTNAMES.has(window.location.hostname);
};

/** Idempotently load gtag.js + push the initial config. Safe to call twice. */
export const initAnalytics = () => {
  if (initialized) return;
  if (!MEASUREMENT_ID || !isProductionHost()) return;
  initialized = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  // send_page_view: false — we send page_view manually per SPA route change
  // so the SPA navigation counts match the browser's URL bar.
  gtag("config", MEASUREMENT_ID, { send_page_view: false });
};

/** Send a page_view event for the given SPA location. */
export const trackPageview = (path, title) => {
  if (!initialized || typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title || document.title,
  });
};
